import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, RotateCcw, ToggleLeft, ToggleRight } from 'lucide-react';
import api from '../api/axios';
import { useTranslation } from 'react-i18next';

export default function ChargeStrategiesPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [strategy, setStrategy] = useState(null); // null = not loaded yet
  const [isActive, setIsActive] = useState(false);
  const [thresholdMinutes, setThresholdMinutes] = useState(10);
  const [noDataThreshold, setNoDataThreshold] = useState(1);
  const [ranges, setRanges] = useState([
    { minLateDays: 1, maxLateDays: 3, chargePercentage: 1.0 },
    { minLateDays: 4, maxLateDays: 7, chargePercentage: 1.5 },
    { minLateDays: 8, maxLateDays: 999, chargePercentage: 2.0 },
  ]);
  const [saving, setSaving] = useState(false);

  const companyId = localStorage.getItem("companyId");

  // Load existing strategy
  useEffect(() => {
    const fetchStrategy = async () => {
      if (!companyId) return;
      try {
        setLoading(true);
        const res = await api.get(`/companies/${companyId}/charge_strategy`);
        const data = res.data?.data || [];

        // For now we support only one latency-based strategy
        const latencyStrat = data.find(s => s.type === "LATENCY_AMOUNT_BASED");
        if (latencyStrat) {
          setStrategy(latencyStrat);
          setIsActive(latencyStrat.isActiveStrategy ?? false);
          setThresholdMinutes(latencyStrat.thresholdMinutes ?? 10);
          setNoDataThreshold(latencyStrat.noDataThreshold ?? 1);
          setRanges(latencyStrat.chargeRange?.length > 0 
            ? latencyStrat.chargeRange 
            : ranges); // fallback to defaults
        }
      } catch (err) {
        console.error(err);
        // It's ok if 404 → we will create new one on save
      } finally {
        setLoading(false);
      }
    };

    fetchStrategy();
  }, [companyId]);

  const addRange = () => {
    setRanges(prev => [
      ...prev,
      { minLateDays: '', maxLateDays: '', chargePercentage: '' }
    ]);
  };

  const removeRange = (index) => {
    setRanges(prev => prev.filter((_, i) => i !== index));
  };

  const updateRange = (index, field, value) => {
    setRanges(prev => 
      prev.map((r, i) => 
        i === index ? { ...r, [field]: value } : r
      )
    );
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);

    // Basic validation
    if (ranges.some(r => 
      !r.minLateDays || !r.maxLateDays || !r.chargePercentage ||
      isNaN(r.minLateDays) || isNaN(r.maxLateDays) || isNaN(r.chargePercentage) ||
      Number(r.minLateDays) < 0 || Number(r.maxLateDays) < Number(r.minLateDays)
    )) {
      setSaving(false);
      return;
    }

    const payload = {
      type: "LATENCY_AMOUNT_BASED",
      thresholdMinutes: Number(thresholdMinutes) || 0,
      noDataThreshold: Number(noDataThreshold) || 0,
      chargeRange: ranges.map(r => ({
        minLateDays: Number(r.minLateDays),
        maxLateDays: Number(r.maxLateDays),
        chargePercentage: Number(r.chargePercentage),
      })),
      companyId,
      isActiveStrategy: isActive,
    };

    try {
      let res;
      if (strategy?._id) {
        res = await api.put(`/companies/${companyId}/charge_strategy`, {
          ...payload
        });
      } else {
        res = await api.put(`/companies/${companyId}/charge_strategy`, payload);
      }

      setStrategy(res.data?.data || payload);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || t('failedToSaveStrategy'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-10 text-center text-gray-300">{t('loading')}</div>;
  }

  if (error) {
    return <div className="p-10 text-center text-red-400">{error}</div>;
  }

  return (
    <div className="p-6 text-white min-h-screen">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-blue-400">{t('chargeStrategies')}</h1>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all duration-200 shadow-lg ${
              saving 
                ? 'bg-gray-700 cursor-not-allowed text-gray-400' 
                : 'bg-sky-600 hover:bg-sky-500 text-white shadow-sky-900/20 active:scale-95'
            }`}
          >
            <Save size={18} />
            {saving ? t('saving') : t('saveChanges')}
          </button>
        </div>

        {/* Main Strategy Card - Latency Amount Based */}
        <div className="bg-gray-800 rounded-2xl shadow p-6 mb-10 border border-gray-700">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-xl font-semibold text-blue-300">
                {t('latencyAmountBasedCharge')}
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                {t('chargeSalaryBasedOnLateDaysPerMonth')}
              </p>
            </div>

            <label className="relative inline-flex items-center cursor-pointer mt-1">
                <input 
                    type="checkbox" 
                    checked={isActive} 
                    onChange={() => setIsActive(prev => !prev)} 
                    className="sr-only peer" 
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-600"></div>
            </label>
          </div>

          {/* Threshold */}
          <div className="mb-6 bg-gray-900 p-4 rounded-xl border border-gray-700">
            <label className="block text-gray-300 mb-2 font-medium">
              {t('excusableLateMinutes')}
            </label>
            <input
              type="number"
              min="0"
              value={thresholdMinutes}
              onChange={e => setThresholdMinutes(e.target.value)}
              className="w-32 px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
              placeholder="10"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('latenessBelowThisIsExcused')}
            </p>
          </div>

          <div className="mb-6 bg-gray-900 p-4 rounded-xl border border-gray-700">
            <label className="block text-gray-300 mb-2 font-medium">
              {t('excusableNoDataAmount')}
            </label>
            <input
              type="number"
              min="0"
              value={noDataThreshold}
              onChange={e => setNoDataThreshold(e.target.value)}
              className="w-32 px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
              placeholder="10"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('excusableNoDataAmountTooltip')}
            </p>
          </div>

          {/* Charge Ranges Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-700 text-gray-300">
                <tr>
                  <th className="px-4 py-3 text-left">{t('fromLateDays')}</th>
                  <th className="px-4 py-3 text-left">{t('toLateDays')}</th>
                  <th className="px-4 py-3 text-left">{t('chargePercentage')}</th>
                  <th className="px-4 py-3 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {ranges.map((range, index) => (
                  <tr key={index} className="hover:bg-gray-900 transition">
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min="0"
                        value={range.minLateDays}
                        onChange={e => updateRange(index, 'minLateDays', e.target.value)}
                        className="w-full bg-gray-700 text-white px-3 py-1.5 rounded border border-gray-600 focus:outline-none focus:border-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min="1"
                        value={range.maxLateDays}
                        onChange={e => updateRange(index, 'maxLateDays', e.target.value)}
                        className="w-full bg-gray-700 text-white px-3 py-1.5 rounded border border-gray-600 focus:outline-none focus:border-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={range.chargePercentage}
                        onChange={e => updateRange(index, 'chargePercentage', e.target.value)}
                        className="w-full bg-gray-700 text-white px-3 py-1.5 rounded border border-gray-600 focus:outline-none focus:border-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => removeRange(index)}
                        className="text-red-400 hover:text-red-300 transition"
                        title={t('removeRange')}
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}

                <tr>
                  <td colSpan={4} className="px-4 py-4">
                    <button
                      onClick={addRange}
                      className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition"
                    >
                      <Plus size={18} /> {t('addChargeRange')}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Future placeholder for other strategies */}
        <div className="bg-gray-800/50 rounded-2xl p-6 text-center text-gray-500 border border-gray-700 border-dashed">
          <p>{t('moreStrategiesComingSoon')}</p>
        </div>
      </div>
    </div>
  );
}